import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { featureAccessApi } from '@/services/featureAccessApi';
import { PlatformFeature, FeatureAccessInfo } from '@/hooks/useFeatureAccess';

interface FeatureAccessState {
  features: FeatureAccessInfo[];
  featuresMap: Record<string, FeatureAccessInfo>;
  loading: boolean;
  initialized: boolean;
  error: Error | null;
  
  // Actions
  loadFeatureAccess: () => Promise<void>;
  setFeatureAccess: (features: FeatureAccessInfo[], featuresMap: Record<string, FeatureAccessInfo>) => void;
  checkFeature: (feature: PlatformFeature) => boolean;
  getFeatureInfo: (feature: PlatformFeature) => FeatureAccessInfo | null;
  clearFeatureAccess: () => void;
}

export const useFeatureAccessStore = create<FeatureAccessState>()(
  devtools(
    immer((set, get) => ({
      features: [],
      featuresMap: {},
      loading: false,
      initialized: false,
      error: null,

      loadFeatureAccess: async () => {
        const state = get();
        if (state.loading) {
          console.log('[FeatureAccessStore] Already loading, skipping duplicate call');
          return; // Prevent duplicate calls
        }

        console.log('[FeatureAccessStore] Loading feature access...');
        set((draft) => {
          draft.loading = true;
          draft.error = null;
        });

        try {
          const response = await featureAccessApi.getMyFeatureAccess();
          const featuresMap: Record<string, FeatureAccessInfo> = {};
          
          response.features.forEach(feature => {
            featuresMap[feature.feature] = feature;
          });

          console.log(`[FeatureAccessStore] Successfully loaded ${response.features.length} features`);
          set((draft) => {
            draft.features = response.features;
            draft.featuresMap = featuresMap;
            draft.initialized = true;
            draft.loading = false;
          });
        } catch (error: any) {
          console.error('[FeatureAccessStore] ❌ Error loading feature access:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url
          });
          set((draft) => {
            draft.error = error as Error;
            draft.loading = false;
            draft.initialized = true; // Mark as initialized even on error to prevent infinite retries
            draft.features = []; // Explicitly set empty array
          });
        }
      },

      setFeatureAccess: (features: FeatureAccessInfo[], featuresMap: Record<string, FeatureAccessInfo>) => {
        set((draft) => {
          draft.features = features;
          draft.featuresMap = featuresMap;
          draft.initialized = true;
          draft.loading = false;
          draft.error = null;
        });
      },

      checkFeature: (feature: PlatformFeature) => {
        const { featuresMap } = get();
        const featureInfo = featuresMap[feature];
        return featureInfo?.hasAccess ?? false;
      },

      getFeatureInfo: (feature: PlatformFeature) => {
        const { featuresMap } = get();
        return featuresMap[feature] || null;
      },

      clearFeatureAccess: () => {
        set((draft) => {
          draft.features = [];
          draft.featuresMap = {};
          draft.loading = false;
          draft.initialized = false;
          draft.error = null;
        });
      },
    }))
  )
);

